import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import PaginationElement from '@/views/elements/pagination/PaginationElement';
import { Box, styled } from '@mui/system';
import { Stack, TableHead, TableSortLabel, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import TypographyElement from '@/views/elements/typography/TypographyElement';
import { ResponseSuccess, ResponseSuccessPagination } from '@/controllers/gridManager/followerTransManager/followerTransManager';
import { isEmpty } from 'lodash';
import ViewNumberFormat from '@/views/elements/Inputs/ViewNumberFormat';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import DatePickerElement from '@/views/elements/datePicker/DatePickerElement';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs, { Dayjs } from 'dayjs';
import { formatDate } from '@/utilities';
import Formula from '@hapi/formula';
import CheckboxElement from '@/views/elements/checkBox/CheckBox';
import { useTranslation } from 'react-i18next';

export interface Column {
    id: string
    label: string;
    enableSort?: boolean
    minWidth?: number;
    width?: number;
    align?: 'right' | 'left' | 'center';
    alignCell?: 'right' | 'left' | 'center';
    format?: (value: number) => string;
    formatNumber?: boolean,
    isPercentage?: boolean,
    isCheckPNL?: boolean, // check màu lãi lỗ
    // customRender
    customRender?: (value: any) => React.ReactNode
    cellFormula?: any//'sum' | 'avg'
    cellSpecial?: string // với trường hợp có cellFormula và giá vốn = 0 mặc định 100%
}

export default function TableUIElement({ columns, fnCallApi, objParameterFn,
    enableDatePicker, queryBeforeDate, queryAfterDate, listenerQuery, firstEndRowSupport,
    firstColumnId, endColumnId, fieldCheck,
    defaultOrderBy, defaultSort, supportCheckBox, rowKey,
    setSelectedToParent,
}: {
    enableDatePicker?: boolean, columns: Column[],
    fnCallApi: (...args: any) => Promise<ResponseSuccess>,
    objParameterFn?: Partial<Parameters<typeof fnCallApi>[0]>,
    queryBeforeDate?: () => React.ReactNode,
    queryAfterDate?: () => React.ReactNode,
    listenerQuery?: any,


    firstEndRowSupport?: boolean,
    firstColumnId?: string
    endColumnId?: string
    fieldCheck?: string

    defaultOrderBy?: string
    defaultSort?: 'asc' | 'desc',

    supportCheckBox?: boolean
    rowKey?: string //supportCheckBox = true => require rowKey
    setSelectedToParent?: ([]) => void  //supportCheckBox = true => require rowKey
}) {

    const { t } = useTranslation()
    const theme = useTheme()

    const [rows, setRows] = React.useState<any[]>([]);
    const [order, setOrder] = React.useState<Order>(defaultSort ?? 'asc');
    const [orderBy, setOrderBy] = React.useState<string>(defaultOrderBy ?? 'size');

    const [total, setTotal] = React.useState<number>(25)
    const [pageSize, setPageSize] = React.useState<number>(20)
    const [pageIndex, setPageIndex] = React.useState<number>(1)

    const [toDate, setToDate] = React.useState<Dayjs | null>(dayjs(new Date()));
    const [fromDate, setFromDate] = React.useState<Dayjs | null>(dayjs(toDate).subtract(1, 'M'));

    const [selected, setSelected] = React.useState<readonly number[]>([]);

    const handleChangeSetToDate = (newValue: Dayjs | null) => {
        setPageIndex(1);
        setToDate(newValue)
    }

    const handleChangeSetFromDate = (newValue: Dayjs | null) => {
        setPageIndex(1);
        setFromDate(newValue)
    }

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        const nPage = newPage + 1
        console.log('ha_check_newPage', nPage);
        setPageIndex(nPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageIndex(1);
    };

    const safePage = (index: number) => {
        return (index - 1) < 0 ? 0 : (index - 1)
    }

    const handleSelectTable = (newSelected: any) => {
        setSelected(newSelected)
        setSelectedToParent && setSelectedToParent(newSelected)
    }

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (rowKey && supportCheckBox === true) {
            if (event.target.checked) {
                const newSelected = rows.map((n) => n[rowKey]);
                console.log('ha_check_handleSelectAllClick', newSelected)
                handleSelectTable(newSelected);
                return;
            }
            handleSelectTable([]);
        }
    };

    const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
        if (rowKey && supportCheckBox === true) {
            const selectedIndex = selected.indexOf(id);
            console.log('ha_check_handleClick', { selected, id, selectedIndex })
            let newSelected: readonly number[] = [];

            if (selectedIndex === -1) {
                newSelected = newSelected.concat(selected, id);
            } else if (selectedIndex === 0) {
                newSelected = newSelected.concat(selected.slice(1));
            } else if (selectedIndex === selected.length - 1) {
                newSelected = newSelected.concat(selected.slice(0, -1));
            } else if (selectedIndex > 0) {
                newSelected = newSelected.concat(
                    selected.slice(0, selectedIndex),
                    selected.slice(selectedIndex + 1),
                );
            }
            handleSelectTable(newSelected);
        }
    };

    React.useEffect(() => {
        async function callApiGetRowsData() {
            const injectParam: Parameters<typeof fnCallApi>[0] = {
                ...objParameterFn,
                fromDate: formatDate(fromDate),
                toDate: formatDate(toDate),
                pageIndex,
                pageSize,
            }
            const { isSuccess, data } = await fnCallApi(!isEmpty(injectParam) ? injectParam : {})
            const rs = data as ResponseSuccessPagination

            console.log('ha_check_callApiGetRowsData', { isSuccess, data });
            if (isSuccess) {
                setRows(rs.data)
                setPageSize(rs.pageSize)
                setTotal(rs.total)
                setPageIndex(rs.pageIndex)
            }
        }

        if (pageSize === 0) return;

        callApiGetRowsData()
    }, [pageSize, pageIndex, fromDate, toDate, listenerQuery])

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: theme.palette.table?.header,
            color: theme.palette.common.white,
        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: 14,
        },
    }));

    const StyledTableRow = styled(TableRow)(({ theme }) => ({
        '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.table?.odd,
        },
        // hide last border
        '&:last-child td, &:last-child th': {
            border: 0,
        },

    }));

    interface EnhancedTableProps {
        onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
        order: Order;
        orderBy: string;

        numSelected: number;
        onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
        rowCount: number;
    }

    function EnhancedTableHead(props: EnhancedTableProps) {
        const { order, orderBy, onRequestSort, onSelectAllClick, rowCount, numSelected } =
            props;
        const createSortHandler =
            (property: string) => (event: React.MouseEvent<unknown>) => {
                onRequestSort(event, property);
            };

        return (
            <TableHead>
                <TableRow>
                    {supportCheckBox === true && rowKey && (
                        <StyledTableCell padding="checkbox">
                            <CheckboxElement
                                color="primary"
                                indeterminate={numSelected > 0 && numSelected < rowCount}
                                checked={rowCount > 0 && numSelected === rowCount}
                                onChange={onSelectAllClick}
                                inputProps={{
                                    'aria-label': 'select all desserts',
                                }}
                            />
                        </StyledTableCell>
                    )}
                    {columns.map((headCell) => (
                        <StyledTableCell
                            key={headCell.id}
                            sortDirection={orderBy === headCell.id ? order : false}
                            align={headCell.align}
                            style={{ minWidth: headCell.minWidth, width: headCell.width }}
                            sx={{ padding: "12px 16px" }}
                        >
                            {headCell.enableSort === true ? <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={createSortHandler(headCell.id)}
                            >
                                <TypographyElement TextAlignElement={headCell.align} FontSizeElement='12px' FontWeightElement={600}>{headCell.label}</TypographyElement>
                                {orderBy === headCell.id ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel> : (
                                <TypographyElement TextAlignElement={headCell.align} FontSizeElement='12px' FontWeightElement={600}>{headCell.label}</TypographyElement>
                            )}
                        </StyledTableCell>
                    ))}
                </TableRow>
            </TableHead>
        );
    }

    function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
        if (b[orderBy] < a[orderBy]) {
            return -1;
        }
        if (b[orderBy] > a[orderBy]) {
            return 1;
        }
        return 0;
    }

    type Order = 'asc' | 'desc';

    function getComparator<Key extends keyof any>(
        order: Order,
        orderBy: Key,
    ): (
        a: { [key in Key]: number | string },
        b: { [key in Key]: number | string },
    ) => number {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    }

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: string,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const visibleRows = React.useMemo(
        () =>
            [...rows]
                .sort(getComparator(order, orderBy)),
        [rows, order, orderBy, pageIndex, pageSize],
    );

    const cellRender = (column: Column, row: any) => {
        const value = row[column.id];
        const id = column.id

        if (column?.cellFormula) {
            if (column?.cellSpecial && row[column?.cellSpecial] === 0) {
                return <TableCell sx={{ padding: "12px 16px" }}
                    key={id} align={column.alignCell} >
                    {
                        <ViewNumberFormat value={100} displayType='text' isPercentage={column?.isPercentage === true} isCheckPNL={column?.isCheckPNL} />
                    }
                </TableCell >
            }

            const formula = new Formula.Parser(column?.cellFormula);
            const calculatorValue = formula.evaluate(row)
            console.log('ha_check_calculatorValue', row, calculatorValue, column?.cellFormula)
            return <TableCell sx={{ padding: "12px 16px" }}
                key={id} align={column.alignCell} >
                {
                    <ViewNumberFormat value={calculatorValue} displayType='text' isPercentage={column?.isPercentage === true} isCheckPNL={column?.isCheckPNL} />
                }
            </TableCell >
        }

        const ids = id.split('|')
        const joinValue = ids?.length > 1 ? ids.map(item => `${row[item]}`).join(' | ') : ''

        if (column?.customRender && (typeof column?.customRender === 'function')) {
            return <TableCell sx={{ padding: "12px 16px" }}
                key={id} align={column.alignCell} >
                {
                    column.customRender(value)
                }
            </TableCell >
        }

        return (
            <TableCell sx={{ padding: "12px 16px" }}
                key={id} align={column.alignCell} >
                {
                    column.formatNumber === true ? (
                        column?.isCheckPNL === true ? (
                            <TypographyElement TextAlignElement={column.alignCell ?? 'end'} ColorElement={value > 0 ? "up" : "down"}>
                                <ViewNumberFormat value={value} displayType='text' isPercentage={column?.isPercentage === true} />
                            </TypographyElement>
                        ) :
                            <ViewNumberFormat value={value} displayType='text' isPercentage={column?.isPercentage === true} />
                    ) : ids?.length > 1 ? joinValue : value
                }
            </TableCell >
        )
    }

    return (
        <>
            {
                enableDatePicker && <Stack direction={'row'} spacing={2}>
                    {
                        queryBeforeDate && queryBeforeDate()
                    }

                    {
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer sx={{ padding: "0px 0px 20px 0px" }} components={['DatePicker']}>
                                <DatePickerElement
                                    value={fromDate}
                                    onChange={(newValue) => handleChangeSetFromDate(newValue)}
                                    label={t('copytrade.common.fromDate')} />
                                <DatePickerElement
                                    value={toDate}
                                    onChange={(newValue) => handleChangeSetToDate(newValue)}
                                    label={t('copytrade.common.toDate')} />
                            </DemoContainer>
                        </LocalizationProvider>
                    }

                    {
                        queryAfterDate && queryAfterDate()
                    }
                </Stack>
            }

            <TableContainer sx={{ height: enableDatePicker === true ? "calc(100% - 50px)" : "100%" }}>
                <Table stickyHeader aria-label="sticky table" size='small'>
                    <EnhancedTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        onSelectAllClick={handleSelectAllClick}
                        numSelected={selected.length}
                        rowCount={rows.length}
                    />
                    <TableBody>

                        {visibleRows
                            .map((row, index) => {
                                const isItemSelected = (supportCheckBox === true && rowKey) ? selected.includes(row[rowKey]) : false;

                                if (firstEndRowSupport === true && fieldCheck && firstColumnId && index === 0 && row[fieldCheck] === firstColumnId) {
                                    return <TableRow>
                                        <TableCell align='right' colSpan={(columns.length - 2 > 0) ? columns.length - 2 : 1} sx={{ padding: "12px 16px" }}
                                            key={'first-1'} >
                                            {
                                                t('copytrade.followerHistoryTransactions.first')
                                            }
                                        </TableCell>
                                        <TableCell align='right' sx={{ padding: "12px 16px" }}
                                            key={'first-2'} >
                                            {
                                                <ViewNumberFormat value={visibleRows?.[0]?.increase ?? 0} displayType='text' />
                                            }
                                        </TableCell>
                                        <TableCell align='right' sx={{ padding: "12px 16px" }}
                                            key={'first-3'} >
                                            {
                                                <ViewNumberFormat value={visibleRows?.[0]?.decrease ?? 0} displayType='text' />
                                            }
                                        </TableCell>
                                    </TableRow>
                                }

                                if (firstEndRowSupport === true && fieldCheck && endColumnId && (index === (visibleRows.length - 1)) && row[fieldCheck] === endColumnId) {
                                    return <TableRow>
                                        <TableCell align='right' colSpan={(columns.length - 2 > 0) ? columns.length - 2 : 1} sx={{ padding: "12px 16px" }}
                                            key={'end-1'} >
                                            {
                                                t('copytrade.followerHistoryTransactions.end')
                                            }
                                        </TableCell>
                                        <TableCell align='right' sx={{ padding: "12px 16px" }}
                                            key={'end-2'} >
                                            {
                                                <ViewNumberFormat value={visibleRows?.[0]?.increase ?? 0} displayType='text' />
                                            }
                                        </TableCell>
                                        <TableCell align='right' sx={{ padding: "12px 16px" }}
                                            key={'end-3'} >
                                            {
                                                <ViewNumberFormat value={visibleRows?.[0]?.decrease ?? 0} displayType='text' />
                                            }
                                        </TableCell>
                                    </TableRow>
                                }


                                return (
                                    <StyledTableRow tabIndex={-1} key={row.code}>
                                        {supportCheckBox === true && rowKey && (
                                            <TableCell padding="checkbox">
                                                <CheckboxElement
                                                    color="primary"
                                                    onClick={(event: React.MouseEvent<unknown>) => handleClick(event, row[rowKey])}
                                                    checked={isItemSelected}
                                                // inputProps={{
                                                //     'aria-labelledby': labelId,
                                                // }}
                                                />
                                            </TableCell>
                                        )}
                                        {columns.map((column) => {
                                            return cellRender(column, row)
                                        })}
                                    </StyledTableRow>
                                );
                            })}

                    </TableBody>
                </Table>
            </TableContainer>


            <PaginationElement
                component="div"
                count={total}
                page={safePage(pageIndex)}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </>
    );
}
